import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from '@/lib/analytics';
import {
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  Network,
  AlertTriangle,
  ShieldCheck,
  Layers,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Blinded case study — a large US public school district (WCPSS / Wake County),
// not yet named publicly. Honest framing: this is a live production deployment
// on Oracle EBS. Only real, sourced facts are used (deployment scale + scope);
// no fabricated ROI. Quote is real, blinded with permission. Un-blind on all-clear.
const CustomerSchoolDistrict = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'How a large US public school district automated invoice processing and AP on Oracle E-Business Suite with EZ Cloud',
    description:
      'A large US K-12 school district replaced manual invoice processing and AP with EZ Cloud invoice automation on Oracle E-Business Suite — live in production with fund accounting, multi-fund coding, district tax handling, and in-workflow receiving.',
    author: { '@type': 'Organization', name: 'EZ Cloud' },
    publisher: {
      '@type': 'Organization',
      name: 'EZ Cloud',
      logo: { '@type': 'ImageObject', url: 'https://ezcloud.ai/ezcloud-logo-v2.png' },
    },
    datePublished: '2026-05-28',
    mainEntityOfPage: 'https://ezcloud.ai/customers/school-district-oracle-ebs',
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="School District on Oracle EBS — Invoice & AP Automation Case Study | EZ Cloud"
        description="How a large US public school district replaced manual invoice processing and AP with EZ Cloud invoice automation on Oracle E-Business Suite — live in production with fund accounting, multi-fund coding, district tax handling, and in-workflow receiving."
        canonical="https://ezcloud.ai/customers/school-district-oracle-ebs"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Customers', url: 'https://ezcloud.ai/customers' },
          { name: 'School district on Oracle EBS', url: 'https://ezcloud.ai/customers/school-district-oracle-ebs' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <GraduationCap className="w-4 h-4" aria-hidden />
              Case Study · K-12 Education · Oracle E-Business Suite
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              A large US public school district
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl">
              From manual AP to a live Oracle EBS deployment — fund accounting, multi-fund coding,
              district tax handling, and in-workflow receiving, posted natively into Oracle.
            </p>

            {/* Deployment facts — sourced, no fabricated ROI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              {[
                { value: 'Live', label: 'In production on Oracle EBS', icon: CheckCircle2 },
                { value: '~25', label: 'Schools & departments', icon: Network },
                { value: '~48', label: 'Users onboarded', icon: GraduationCap },
                { value: '0', label: 'Custom EBS schema (standard interface)', icon: ShieldCheck },
              ].map(({ value, label, icon: Icon }) => (
                <div key={label} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                  <Icon className="w-5 h-5 text-accent mb-2" aria-hidden />
                  <div className="text-3xl font-bold text-accent mb-1">{value}</div>
                  <div className="text-sm text-white/70">{label}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/50 mt-4">
              Initial production rollout figures. We'll publish efficiency outcomes as the deployment matures.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-4 max-w-4xl py-16 space-y-12">
          {/* At a glance */}
          <section aria-labelledby="glance">
            <h2 id="glance" className="text-2xl font-semibold text-gray-900 mb-4">
              At a glance
            </h2>
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div>
                  <dt className="text-gray-500">Industry</dt>
                  <dd className="text-gray-900 font-medium">K-12 public education (US)</dd>
                </div>
                <div>
                  <dt className="text-gray-500">ERP</dt>
                  <dd className="text-gray-900 font-medium">Oracle E-Business Suite (EBS 12.2)</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Before EZ Cloud</dt>
                  <dd className="text-gray-900 font-medium">Manual AP processing</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="text-gray-900 font-medium">Live in production (since early 2026)</dd>
                </div>
              </dl>
            </Card>
          </section>

          {/* Challenge */}
          <section aria-labelledby="challenge">
            <h2 id="challenge" className="text-2xl font-semibold text-gray-900 mb-4">
              The challenge
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              A large public school district processed accounts payable manually across dozens of
              schools and departments. Public-sector AP adds requirements a generic tool doesn't
              handle: fund accounting, multi-fund coding, district-specific tax rules, and tight
              period and approval controls — all posting into Oracle E-Business Suite.
            </p>
            <ul className="space-y-3">
              {[
                'Manual AP keying across dozens of schools and departments',
                'Fund accounting and multi-fund invoice coding, batched per fund',
                'District tax handling — local, state, transit, and prepared-food tax',
                'Closed-period and prior-year invoice-date controls',
                'Approval thresholds and attestation requirements for public accountability',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-gray-600">
                  <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Solution */}
          <section aria-labelledby="solution">
            <h2 id="solution" className="text-2xl font-semibold text-gray-900 mb-4">
              The solution
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              EZ Cloud deployed against the district's Oracle E-Business Suite using an integration
              server in the district's own environment, writing to the standard Payables Open
              Interface — no custom EBS schema. The platform handles capture, validation, approval
              routing, and posting, with the public-sector specifics built in.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {
                  icon: Network,
                  title: 'Native Oracle EBS posting',
                  body: 'Writes to the standard AP invoice interface and runs Payables Open Interface Import — no custom schema, no base-table writes.',
                },
                {
                  icon: Layers,
                  title: 'Fund accounting & multi-fund coding',
                  body: 'Invoices coded and batched per fund, aligned to how a public district closes its books.',
                },
                {
                  icon: ShieldCheck,
                  title: 'District tax handling',
                  body: 'Local, state, transit, and prepared-food tax split correctly on the invoice before posting.',
                },
                {
                  icon: CheckCircle2,
                  title: 'In-workflow receiving & controls',
                  body: 'Goods receiving inside the workflow, approval thresholds, attestation, and overdue-invoice notifications for public accountability.',
                },
              ].map((item) => (
                <Card key={item.title} className="p-5 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-3">
                    <item.icon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Results */}
          <section aria-labelledby="results">
            <h2 id="results" className="text-2xl font-semibold text-gray-900 mb-6">
              Where it stands
            </h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">Live in production on Oracle EBS</h3>
                <p>
                  The district went live on EZ Cloud in early 2026, replacing manual AP with end-to-end
                  invoice automation — AI invoice data extraction, multi-fund coding, approval routing,
                  and posting into Oracle E-Business Suite.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">
                  ~25 schools and departments, ~48 users
                </h3>
                <p>
                  The initial rollout spans roughly 25 schools and departments and ~48 users across
                  bookkeeping, budget management, AP, and approval roles.
                </p>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-2">Built for public-sector controls</h3>
                <p>
                  Fund batching, district tax handling, approval thresholds, attestation, and a
                  complete audit trail — the requirements a public district answers to, handled in the
                  workflow.
                </p>
              </div>
            </div>
          </section>

          {/* Quote (blinded, with permission) */}
          <section aria-labelledby="quote">
            <h2 id="quote" className="sr-only">Customer quote</h2>
            <Card className="p-8 bg-accent/10 border-accent/30">
              <blockquote className="text-lg md:text-xl text-gray-900 leading-relaxed italic mb-4">
                "I sincerely appreciate your willingness to move forward with the automated receipt
                functionality at no additional cost. That gesture demonstrates your commitment to our
                partnership and to the successful rollout of EZ Cloud."
              </blockquote>
              <footer className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-accent" aria-hidden />
                <div>
                  <div className="text-gray-900 font-semibold">Assistant Finance Officer</div>
                  <div className="text-sm text-gray-600">US K-12 public school district</div>
                </div>
              </footer>
            </Card>
          </section>

          {/* Related */}
          <section aria-labelledby="related" className="border-t border-gray-200 pt-10">
            <h2 id="related" className="text-2xl font-semibold text-gray-900 mb-6">
              Related EZ Cloud resources
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/solutions/education-oracle-ebs"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Education AP on Oracle EBS
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  The full solution for K-12 and higher-ed running Oracle E-Business Suite — fund accounting, grants, district tax.
                </p>
              </Link>
              <Link
                to="/integrations/oracle"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Oracle EBS &amp; Fusion integration
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  How EZ Cloud posts into Oracle EBS using the standard Payables Open Interface.
                </p>
              </Link>
              <Link
                to="/industries/education"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Education AP automation
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  School-year budget cycles, fund accounting, and grant tracking for K-12 and higher-ed.
                </p>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <section className="border-t border-gray-200 pt-12 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              Run Oracle EBS? See it against your district's AP.
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Book a 30-minute walkthrough and we'll show AP automation on Oracle E-Business Suite —
              fund accounting, district tax, and in-workflow receiving included.
            </p>
            <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
              Book an Oracle AP demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CustomerSchoolDistrict;
